import { useNavigate } from "react-router-dom";
import {
  Calculator,
  TrendingUp,
  PiggyBank,
  Landmark,
  Receipt,
  Baby,
  Wallet,
  CreditCard,
  ArrowUpRight,
  Sparkles,
} from "lucide-react";
import { Card, CardContent } from "@/common/components/ui/card";
import ProductLayout from "@/common/components/ProductLayout";

const Calculators = () => {
  const navigate = useNavigate();

  const categories = [
    {
      id: "investment",
      title: "Investment Planning",
      icon: TrendingUp,
      color: "text-primary",
      bg: "bg-primary/10",
      items: [
        {
          id: "sip",
          title: "SIP Calculator",
          desc: "Plan your mutual fund investments",
          icon: TrendingUp,
        },
        {
          id: "lumpsum",
          title: "Lumpsum Calculator",
          desc: "Calculate one-time investment growth",
          icon: Landmark,
        },
        {
          id: "swp",
          title: "SWP Calculator",
          desc: "Systematic Withdrawal Plan",
          icon: ArrowUpRight,
        },
        {
          id: "ppf",
          title: "PPF Calculator",
          desc: "Public Provident Fund returns",
          icon: Wallet,
        },
        {
          id: "epf",
          title: "EPF Calculator",
          desc: "Employee Provident Fund planning",
          icon: Wallet,
        },
        {
          id: "nps",
          title: "NPS Calculator",
          desc: "National Pension Scheme planning",
          icon: Calculator,
        },
        {
          id: "ssy",
          title: "SSY Calculator",
          desc: "Sukanya Samriddhi Yojana",
          icon: Baby,
        },
        {
          id: "retirement",
          title: "Retirement Planning",
          desc: "Plan for your golden years",
          icon: Sparkles,
        },
      ],
    },
    {
      id: "banking",
      title: "Banking & Savings",
      icon: PiggyBank,
      color: "text-primary",
      bg: "bg-primary/10",
      items: [
        {
          id: "fd",
          title: "FD Calculator",
          desc: "Fixed Deposit maturity estimation",
          icon: PiggyBank,
        },
      ],
    },
    {
      id: "taxation",
      title: "Tax Planning",
      icon: Receipt,
      color: "text-primary",
      bg: "bg-primary/10",
      items: [
        {
          id: "tax",
          title: "Income Tax",
          desc: "FY 2024-25 Old vs New Regime",
          icon: Receipt,
        },
        {
          id: "hra",
          title: "HRA Calculator",
          desc: "House Rent Allowance Exemption",
          icon: Landmark,
        },
      ],
    },
    {
      id: "loans",
      title: "Loans & Credit",
      icon: CreditCard,
      color: "text-primary",
      bg: "bg-primary/10",
      items: [
        {
          id: "emi",
          title: "EMI Calculator",
          desc: "Calculate your monthly loan EMI",
          icon: CreditCard,
        },
      ],
    },
    {
      id: "more",
      title: "More Tools",
      icon: TrendingUp,
      color: "text-primary",
      bg: "bg-primary/10",
      items: [
        {
          id: "rd",
          title: "Recurring Deposit",
          desc: "Calculate maturity amount for monthly savings",
          icon: TrendingUp,
        },
        {
          id: "inflation",
          title: "Inflation",
          desc: "Calculate future purchasing power",
          icon: TrendingUp,
        },
        {
          id: "gst",
          title: "GST Calculator",
          desc: "Calculate GST amounts",
          icon: Calculator,
        },
        {
          id: "cagr",
          title: "CAGR Calculator",
          desc: "Calculate Compound Annual Growth Rate",
          icon: TrendingUp,
        },
      ],
    },
  ];

  return (
    <ProductLayout>
      <div className="container mx-auto px-4 py-10">
        <div className="mb-10 text-center">
          <h1 className="mb-4 text-4xl font-bold text-secondary">
            Financial Calculators
          </h1>
          <p className="mx-auto max-w-2xl text-muted-foreground">
            Plan your financial future with our comprehensive suite of
            calculators.
          </p>
        </div>

        <div className="space-y-12">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {categories
              .flatMap(cat => cat.items)
              .map(item => {
                // Determine colors based on calculator ID
                let colorClass = "bg-primary/5";
                let iconColor = "text-primary";
                let hoverTextClass = "group-hover:text-primary"; // Default safe fallback

                // Explicitly defining classes so Tailwind doesn't purge them
                if (["sip", "swp", "rd"].includes(item.id)) {
                  colorClass = "bg-emerald-50";
                  iconColor = "text-emerald-500";
                  hoverTextClass = "group-hover:text-emerald-600";
                } else if (["ppf", "epf", "ssy"].includes(item.id)) {
                  colorClass = "bg-blue-50";
                  iconColor = "text-blue-500";
                  hoverTextClass = "group-hover:text-blue-600";
                } else if (
                  ["lumpsum", "fd", "nps", "cagr", "inflation"].includes(
                    item.id
                  )
                ) {
                  colorClass = "bg-amber-50";
                  iconColor = "text-amber-500";
                  hoverTextClass = "group-hover:text-amber-600";
                } else if (
                  ["emi", "tax", "hra", "gst", "retirement"].includes(item.id)
                ) {
                  colorClass = "bg-purple-50";
                  iconColor = "text-purple-500";
                  hoverTextClass = "group-hover:text-purple-600";
                }

                return (
                  <Card
                    key={item.id}
                    onClick={() => navigate(`/calculators/${item.id}`)}
                    className="group relative h-[180px] cursor-pointer overflow-hidden rounded-[1.5rem] border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-gray-300 hover:shadow-xl"
                  >
                    <CardContent className="relative z-10 flex h-full flex-col p-6">
                      <div className="mb-2">
                        <h3
                          className={`text-lg font-bold text-slate-800 ${hoverTextClass} mb-2 tracking-tight transition-colors duration-300`}
                        >
                          {item.title}
                        </h3>
                        <p className="line-clamp-2 pr-8 text-xs font-medium leading-relaxed text-slate-500 opacity-80">
                          {item.desc}
                        </p>
                      </div>
                    </CardContent>

                    {/* Decorative Background Blob */}
                    <div
                      className={`absolute -bottom-12 -right-12 h-40 w-40 rounded-full opacity-0 transition-opacity duration-500 group-hover:opacity-100 ${colorClass.replace("bg-", "bg- bg-opacity-40")}`}
                    />

                    {/* Illustration Icon */}
                    <div
                      className={`absolute bottom-5 right-5 rounded-2xl p-3 ${colorClass} border border-${iconColor.split("-")[1]}-100 transition-all duration-500 group-hover:-translate-y-1 group-hover:scale-110 group-hover:shadow-sm`}
                    >
                      <item.icon
                        className={`h-8 w-8 ${iconColor} stroke-[1.5]`}
                      />
                    </div>
                  </Card>
                );
              })}
          </div>
        </div>
      </div>
    </ProductLayout>
  );
};

export default Calculators;
